<template>
  <div class="tw-flex tw-flex-col tw-h-full tw-min-h-0">
    <!-- Thread messaggi -->
    <div class="tw-flex-1 tw-overflow-y-auto tw-pr-1 tw-space-y-3 tw-py-1">
      <p
        v-if="!messages.length"
        class="tw-text-sm tw-text-slate-400 tw-text-center tw-py-6"
      >
        Nessun messaggio.
      </p>

      <div
        v-for="m in messages"
        :key="m.id"
        class="tw-flex"
        :class="isSelf(m) ? 'tw-justify-end' : 'tw-justify-start'"
      >
        <div
          class="tw-max-w-[80%] tw-rounded-lg tw-px-3 tw-py-2"
          :class="bubbleClass(m)"
        >
          <div class="tw-flex tw-items-center tw-gap-2 tw-mb-0.5">
            <span class="tw-text-[11px] tw-font-semibold">{{
              senderLabel(m)
            }}</span>
            <span class="tw-text-[10px] tw-opacity-60">{{
              formatDate(m.createdAt)
            }}</span>
          </div>
          <p class="tw-text-sm tw-whitespace-pre-wrap tw-break-words">
            {{ m.body }}
          </p>
          <div
            v-if="m.attachments && m.attachments.length"
            class="tw-flex tw-flex-wrap tw-gap-1 tw-mt-2"
          >
            <a
              v-for="(att, i) in m.attachments"
              :key="i"
              :href="attUrl(att)"
              target="_blank"
              rel="noopener"
            >
              <img
                :src="attUrl(att)"
                class="tw-w-14 tw-h-14 tw-object-cover tw-rounded tw-border tw-border-black/10 hover:tw-opacity-80"
                alt="allegato"
              />
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Box risposta -->
    <div
      v-if="canReply"
      class="tw-shrink-0 tw-border-t tw-border-slate-200 tw-pt-2 tw-mt-2"
    >
      <div v-if="files.length" class="tw-flex tw-flex-wrap tw-gap-2 tw-mb-2">
        <div
          v-for="f in files"
          :key="f.id"
          class="tw-relative tw-w-14 tw-h-14 tw-rounded tw-border tw-border-slate-300 tw-overflow-hidden"
        >
          <img :src="f.preview" class="tw-w-full tw-h-full tw-object-cover" />
          <button
            type="button"
            class="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-text-[10px] tw-rounded-bl"
            @click="removeFile(f.id)"
          >
            ×
          </button>
        </div>
      </div>
      <div class="tw-flex tw-items-end tw-gap-2">
        <textarea
          v-model="body"
          rows="2"
          placeholder="Scrivi un messaggio…"
          class="tw-flex-1 tw-min-w-0 tw-box-border tw-border tw-border-slate-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-resize-none"
        ></textarea>
        <label
          class="tw-cursor-pointer tw-text-slate-500 hover:tw-text-slate-700 tw-text-xl tw-px-1"
          title="Allega immagine"
        >
          📎
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            class="tw-hidden"
            @change="onFileChange"
          />
        </label>
        <button
          type="button"
          class="tw-bg-fb-accent tw-text-white tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-fb-accent-hover disabled:tw-opacity-60"
          :disabled="sending || (!body.trim() && !files.length)"
          @click="send"
        >
          {{ sending ? "…" : "Invia" }}
        </button>
      </div>
      <p class="tw-text-[11px] tw-text-slate-400 tw-mt-1">
        💡 Ctrl/Cmd+V per incollare un'immagine
      </p>
    </div>

    <div
      v-else
      class="tw-shrink-0 tw-border-t tw-border-slate-200 tw-pt-3 tw-mt-2 tw-text-center"
    >
      <p class="tw-text-xs tw-text-slate-500 tw-mb-2">
        Questo ticket è chiuso.
      </p>
      <slot name="closed" />
    </div>
  </div>
</template>

<script>
import { imageUrl } from "../services/feedbackService";

export default {
  name: "FeedbackConversation",
  props: {
    messages: { type: Array, default: () => [] },
    role: { type: String, default: "user" }, // 'user' | 'admin'
    canReply: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 5 },
    maxFileMb: { type: Number, default: 5 },
  },
  emits: ["send"],
  data() {
    return { body: "", files: [], seq: 0 };
  },
  mounted() {
    document.addEventListener("paste", this.onPaste);
  },
  beforeUnmount() {
    document.removeEventListener("paste", this.onPaste);
  },
  methods: {
    attUrl(att) {
      if (!att) return "";
      const rel =
        typeof att === "string"
          ? att
          : att.url || att.relativePath || att.path || "";
      return imageUrl(rel);
    },
    isSelf(m) {
      return (
        (this.role === "user" && m.senderType === "user") ||
        (this.role === "admin" && m.senderType === "admin")
      );
    },
    bubbleClass(m) {
      if (m.senderType === "system") return "tw-bg-slate-100 tw-text-slate-600";
      return this.isSelf(m)
        ? "tw-bg-fb-accent tw-text-white"
        : "tw-bg-slate-100 tw-text-slate-800";
    },
    senderLabel(m) {
      if (m.senderType === "system") return "Sistema";
      if (m.senderType === "user")
        return this.role === "user" ? "Tu" : "Cliente";
      return this.role === "admin" ? "Tu" : "Team";
    },
    formatDate(d) {
      try {
        return new Date(d).toLocaleString("it-IT");
      } catch {
        return "";
      }
    },
    onFileChange(e) {
      for (const file of e.target.files) this.addFile(file);
      e.target.value = "";
    },
    onPaste(e) {
      if (!this.canReply) return;
      const items =
        (e.clipboardData || e.originalEvent?.clipboardData)?.items || [];
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) this.addFile(file);
        }
      }
    },
    addFile(file) {
      if (!file.type.startsWith("image/")) return;
      if (this.files.length >= this.maxFiles) return;
      if (file.size > this.maxFileMb * 1024 * 1024) return;
      const id = this.seq++;
      const reader = new FileReader();
      reader.onload = (ev) =>
        this.files.push({ id, file, preview: ev.target.result });
      reader.readAsDataURL(file);
    },
    removeFile(id) {
      this.files = this.files.filter((f) => f.id !== id);
    },
    // Chiamata dal PARENT solo dopo invio andato a buon fine.
    resetDraft() {
      this.body = "";
      this.files = [];
    },
    send() {
      const body = this.body.trim();
      if (!body && !this.files.length) return;
      // NON resetto qui: il parent chiamerà resetDraft() solo dopo successo,
      // così in caso di errore il testo e gli allegati restano nel box.
      this.$emit("send", {
        body,
        files: this.files.map((f) => f.file),
        resetDraft: this.resetDraft,
      });
    },
  },
};
</script>
